import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
    fetchTrainings,
    addTraining,
    editTraining,
    deleteTraining,
    saveTrainingsOrder,
    setGlobalPublishTime,
    reorderScheduledTrainings,
    updateTrainingFromStatus
} from '../slices/trainingsSlice';
import { useLanguage } from '../../i18n/LanguageContext';
import type { ITraining, BaseVideoFormData } from '../../types';

// Define a basic interface for trainings state
interface TrainingsState {
    publishedTrainings: ITraining[];
    scheduledTrainings: ITraining[];
    isLoading: boolean;
    isSaving: boolean;
    globalPublishTime: string;
    error: string | null;
}

export const useTrainings = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { language } = useLanguage();
    const currentLanguage = language.toLowerCase() as 'ru' | 'it';

    const {
        publishedTrainings,
        scheduledTrainings,
        isLoading,
        isSaving,
        globalPublishTime,
        error
    } = useSelector((state: RootState) => state.trainings as TrainingsState);

    // Filter trainings by current language
    const filteredPublishedTrainings = publishedTrainings.filter(
        (t: ITraining) => t.language && t.language.toUpperCase() === currentLanguage.toUpperCase()
    );

    const filteredScheduledTrainings = scheduledTrainings.filter(
        (t: ITraining) => t.language && t.language.toUpperCase() === currentLanguage.toUpperCase()
    );

    const loadTrainings = () => {
        dispatch(fetchTrainings(currentLanguage));
    };

    const handleAddTraining = async (data: BaseVideoFormData) => {
        return await dispatch(addTraining({
            training: data.training,
            language: currentLanguage,
            videoFile: data.videoFile,
            thumbnailFile: data.thumbnailFile
        }));
    };

    const handleEditTraining = async (id: number, data: BaseVideoFormData) => {
        await dispatch(editTraining({
            id,
            training: data.training,
            language: currentLanguage,
            videoFile: data.videoFile,
            thumbnailFile: data.thumbnailFile
        }));
    };

    const handleDeleteTraining = async (id: number) => {
        await dispatch(deleteTraining(id));
    };

    const handleReorderTrainings = (activeId: string, overId: string) => {
        dispatch(reorderScheduledTrainings({ activeId, overId }));
    };

    const handleSetGlobalPublishTime = (time: string) => {
        dispatch(setGlobalPublishTime(time));
    };

    const handleSaveTrainingsOrder = async () => {
        return await dispatch(saveTrainingsOrder(currentLanguage));
    };

    const handleUpdateTrainingFromStatus = (trainingId: number, updates: Partial<ITraining>) => {
        dispatch(updateTrainingFromStatus({ trainingId, updates }));
    };

    return {
        publishedTrainings: filteredPublishedTrainings,
        scheduledTrainings: filteredScheduledTrainings,
        allPublishedTrainings: publishedTrainings,
        allScheduledTrainings: scheduledTrainings,
        isLoading,
        isSaving,
        globalPublishTime,
        error,
        loadTrainings,
        addTraining: handleAddTraining,
        editTraining: handleEditTraining,
        deleteTraining: handleDeleteTraining,
        reorderTrainings: handleReorderTrainings,
        setGlobalPublishTime: handleSetGlobalPublishTime,
        saveTrainingsOrder: handleSaveTrainingsOrder,
        updateTrainingFromStatus: handleUpdateTrainingFromStatus
    };
}; 